import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, delay, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { ErrorService } from 'src/app/services/error.service';
import { ExportDataService } from 'src/app/services/export-data.service';
import { TranslationService } from 'src/app/services/translation.service';
import { environment } from 'src/environments/environment';
import * as AdvancedSearchActions from '../actions/adv-search.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class AdvancedSearchEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private store: Store<AppState>,

    private errorService: ErrorService,
    private exportDataService: ExportDataService,
    private translationService: TranslationService
  ) { }

  getAdvancedSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.advancedSearchStart),
      switchMap(({ payload }) => {
        const { filters, ...restQueryParams } = payload.queryParams;
        let query = {};
        query = {
          ...restQueryParams,
          searchFields: JSON.stringify(
            payload.queryParams.filters.searchFields
          ),
          searchOn: payload.queryParams.filters.searchOn,
          logicalOperator: payload.queryParams.filters.logicalOperator,
          nestedResults: true,
        };

        return this.http
          .get<IResponse<any>>(environment.apiUrl + '/advancedSearch/', {
            params: query,
          })
          .pipe(
            tap((response) => {
              console.log('🔎 AdvancedSearch response:', response);
            }),
            mergeMap((response) => {
              return of(
                AdvancedSearchActions.advancedSearchSuccess({
                  payload: {
                    properties: response.data,
                    totalCount: response.totalCount,
                  },
                })
              );
            }),
            catchError((error) => {
              return of(
                AdvancedSearchActions.advancedSearchFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  exportAdvancedSearchResults = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.exportAdvSearchResults),
      switchMap(({ payload }) => {
        this.store.dispatch(
          AdvancedSearchActions.advancedSearchLoading({
            payload: { loading: true },
          })
        );
        let query = {};
        query = {
          ...payload.queryParams,
          searchFields: JSON.stringify(payload.queryParams.searchFields),
          nestedResults: false,
        };

        return this.http
          .get<IResponse<any>>(environment.apiUrl + '/advancedSearch/', {
            params: query,
          })
          .pipe(
            delay(5000),
            map((response) => ({ payload, response })),

            mergeMap((mappedResponse) => {
              this.exportDataService.exportDataToCsv(
                mappedResponse.response.data,
                mappedResponse.payload.fileName,
                mappedResponse.payload.columns
              );

              return of(
                AdvancedSearchActions.advancedSearchLoading({
                  payload: { loading: false },
                })
              );
            }),
            catchError((error) => {
              return of(
                AdvancedSearchActions.advancedSearchFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  exportAdvancedSearchShapefile = createEffect(() =>
    this.actions$.pipe(
      ofType(AdvancedSearchActions.exportAdvSearchShapefile),
      switchMap(({ payload }) => {
        this.store.dispatch(
          AdvancedSearchActions.advancedSearchLoading({
            payload: { loading: true },
          })
        );
        const query = {
          ...payload.queryParams,
          searchFields: JSON.stringify(payload.queryParams.searchFields),
          nestedResults: false,
        };

        return this.http
          .get(environment.apiUrl + '/advancedSearch/shapefile', {
            params: query,
            responseType: 'blob',
          })
          .pipe(
            map((blob: Blob) => {
              this.exportDataService.saveBlobAsFile(blob, `${payload.fileName}.zip`);
              return AdvancedSearchActions.advancedSearchLoading({
                payload: { loading: false },
              });
            }),
            catchError((error) => {
              // 422 = the matched results have no geometry to export -> friendly message.
              if (error?.status === 422) {
                return of(
                  AdvancedSearchActions.advancedSearchFail({
                    payload: {
                      error: {
                        message: 'NO_FEATURES_WITH_GEOMETRY',
                        messageToUser: this.translationService.translate(
                          'ADVANCED_SEARCH.NO_GEOMETRY_TO_EXPORT'
                        ),
                        stack: null,
                      },
                    },
                  })
                );
              }
              return of(
                AdvancedSearchActions.advancedSearchFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );
}
